import React, { useRef, useEffect, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Stage, Layer, Rect, Line, Image as KonvaImage } from 'react-konva';
import RoomShape from './RoomShape';
import { ExternalWalls, InternalWall } from './WallShape';
import EsquadriaShape from './EsquadriaShape';
import DimensionLines from './DimensionLines';
import useCoordinates from '../hooks/useCoordinates';
import { WALL_THICKNESS } from '../constants'; // eslint-disable-line no-unused-vars

/**
 * Main Konva canvas that renders the clean plant.
 * Receives virtual-coordinate rooms, walls, and esquadrias.
 */
const CanvasStage = forwardRef(function CanvasStage({
  rooms,
  intWalls,
  extW,
  extH,
  selectedRoom,
  selectedWall,
  selectedEsq,
  editMode,
  isPan,
  showDimensions,
  showOverlay,
  overlayOpacity,
  overlayImage,
  onSelectRoom,
  onSelectWall,
  onSelectEsq,
  onRoomRightClick,
  onStageClick,
  containerRef,
  hasGeometry,
}, ref) {
  const stageRef = useRef(null);

  // Expose the Konva stage to parent via ref
  useImperativeHandle(ref, () => ({
    getStage: () => stageRef.current,
  }), []);

  const [size, setSize] = useState({ w: 800, h: 600 });

  // Observe container resize
  useEffect(() => {
    if (!containerRef?.current) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0) {
          setSize({ w: width, h: height });
        }
      }
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [containerRef]);

  const { sc, offset, s } = useCoordinates(size.w, size.h, extW, extH);

  // Zoom via scroll wheel
  const handleWheel = useCallback((e) => {
    e.evt.preventDefault();
    const stage = stageRef.current;
    if (!stage) return;

    const oldScale = stage.scaleX();
    const pointer = stage.getPointerPosition();
    const factor = 1.06;
    const newScale = e.evt.deltaY > 0 ? oldScale / factor : oldScale * factor;

    // Clamp scale
    const clampedScale = Math.max(0.3, Math.min(5, newScale));

    stage.scale({ x: clampedScale, y: clampedScale });
    stage.position({
      x: pointer.x - (pointer.x - stage.x()) * (clampedScale / oldScale),
      y: pointer.y - (pointer.y - stage.y()) * (clampedScale / oldScale),
    });
    stage.draw();
  }, []);

  // Load overlay image
  const [overlayImg, setOverlayImg] = useState(null);
  useEffect(() => {
    if (!overlayImage) { setOverlayImg(null); return; }
    const img = new window.Image();
    img.onload = () => setOverlayImg(img);
    img.src = overlayImage;
  }, [overlayImage]);

  // Plant origin (top-left of external walls on canvas)
  const plantX = offset.x;
  const plantY = offset.y;
  const plantW = s(extW);
  const plantH = s(extH);

  return (
    <Stage
      ref={stageRef}
      width={size.w}
      height={size.h}
      draggable={isPan}
      onWheel={handleWheel}
      onClick={(e) => {
        if (e.target === e.target.getStage()) {
          onStageClick && onStageClick(e);
        }
      }}
      style={{ cursor: isPan ? 'grab' : editMode ? 'crosshair' : 'default' }}
    >
      {/* Background layer */}
      <Layer>
        <Rect x={0} y={0} width={size.w} height={size.h} fill="#FFFFFF" listening={false} />
      </Layer>

      {/* Overlay layer (original plant image) */}
      {showOverlay && overlayImg && (
        <Layer opacity={overlayOpacity || 0.15}>
          <KonvaImage
            image={overlayImg}
            x={plantX}
            y={plantY}
            width={plantW}
            height={plantH}
          />
        </Layer>
      )}

      {/* Walls layer */}
      <Layer>
        {/* External walls — only in bbox fallback mode.
            In geometry mode, walls come from Grid Snap polygon edges. */}
        {!hasGeometry && (
          <ExternalWalls x={plantX} y={plantY} w={plantW} h={plantH} />
        )}

        {/* Internal walls */}
        {intWalls.map((wall) => (
          <InternalWall
            key={wall.id}
            wall={wall}
            x1={plantX + s(wall.x1)}
            y1={plantY + s(wall.y1)}
            x2={plantX + s(wall.x2)}
            y2={plantY + s(wall.y2)}
            isSelected={selectedWall === wall.id}
            editMode={editMode}
            onSelect={onSelectWall}
          />
        ))}
      </Layer>

      {/* Rooms layer */}
      <Layer>
        {rooms.map((room, idx) => (
          <RoomShape
            key={room.id || idx}
            room={room}
            index={idx}
            x={plantX + s(room.ax)}
            y={plantY + s(room.ay)}
            w={s(room.aw)}
            h={s(room.ah)}
            isSelected={selectedRoom === idx}
            editMode={editMode}
            onSelect={onSelectRoom}
            onRightClick={onRoomRightClick}
            plantX={plantX}
            plantY={plantY}
            sc={sc}
          />
        ))}
      </Layer>

      {/* Esquadrias layer */}
      <Layer>
        {rooms.map((room, rIdx) =>
          (room.esq || []).map((esq, eIdx) => {
            const px = plantX + s(esq.px || room.ax + room.aw / 2);
            const py = plantY + s(esq.py || room.ay);
            return (
              <EsquadriaShape
                key={`esq-${rIdx}-${eIdx}`}
                esquadria={esq}
                x={px}
                y={py}
                sc={sc}
                isSelected={
                  selectedEsq &&
                  selectedEsq.roomIdx === rIdx &&
                  selectedEsq.esqIdx === eIdx
                }
                editMode={editMode}
                onSelect={() => onSelectEsq && onSelectEsq({ roomIdx: rIdx, esqIdx: eIdx })}
              />
            );
          })
        )}
      </Layer>

      {/* Dimension lines layer */}
      <Layer>
        <DimensionLines
          rooms={rooms}
          extW={extW}
          extH={extH}
          offset={offset}
          sc={sc}
          visible={showDimensions}
        />
      </Layer>

      {/* Highlight layer (selected room glow) */}
      {selectedRoom !== null && rooms[selectedRoom] && (
        <Layer>
          {rooms[selectedRoom].vertices && rooms[selectedRoom].vertices.length >= 3 ? (
            <Line
              points={rooms[selectedRoom].vertices.flatMap(([vx, vy]) => [
                plantX + vx * sc,
                plantY + vy * sc,
              ])}
              closed
              fill="rgba(249,115,22,0.04)"
              stroke="#F97316"
              strokeWidth={2}
              listening={false}
            />
          ) : (
            <Rect
              x={plantX + s(rooms[selectedRoom].ax) + WALL_THICKNESS / 2}
              y={plantY + s(rooms[selectedRoom].ay) + WALL_THICKNESS / 2}
              width={s(rooms[selectedRoom].aw) - WALL_THICKNESS}
              height={s(rooms[selectedRoom].ah) - WALL_THICKNESS}
              fill="rgba(249,115,22,0.04)"
              stroke="#F97316"
              strokeWidth={2}
              listening={false}
            />
          )}
        </Layer>
      )}
    </Stage>
  );
});

export default CanvasStage;